/**
 * F27 (Wave 2E) — admin WhatsApp API client (real surface).
 *
 * Endpoints:
 *   GET    /admin/whatsapp/templates
 *     Perm: `whatsapp:read` (super_admin + support + finance + content_moderator)
 *   POST   /admin/whatsapp/templates/sync
 *     Perm: `whatsapp:write` (super_admin only); rate-limit 5/h
 *   GET    /admin/whatsapp/quality
 *     Perm: `whatsapp:read`
 *   GET    /admin/whatsapp/cost-summary
 *     Perm: `whatsapp:read`
 *   GET    /admin/whatsapp/delivery-log?userId= OR ?bookingId=
 *     Perm: `whatsapp:read`
 *
 * RBAC is enforced server-side. The page-level <ProtectedRoute> gates
 * navigation; the API client forwards requests and surfaces the server's
 * 403 to the caller.
 *
 * For tests, the admin vitest config aliases this module to
 * `./whatsapp.fixture` so component tests drive an in-memory mock.
 */
import type {
  WhatsAppQualityState,
  WhatsAppTemplate,
  WhatsAppTemplateCategory,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

// ─── Templates ──────────────────────────────────────────────────────────────

export interface ListTemplatesResult {
  readonly data: readonly WhatsAppTemplate[];
}

export interface SyncTemplatesResult {
  readonly synced: number;
  readonly remoteCount: number;
  readonly syncedAt: string;
}

/** GET /admin/whatsapp/templates — list registry rows. */
export async function listWhatsAppTemplates(): Promise<ListTemplatesResult> {
  return adminFetch<ListTemplatesResult>('/admin/whatsapp/templates');
}

/** POST /admin/whatsapp/templates/sync — manual sync trigger (super_admin only). */
export async function syncWhatsAppTemplates(): Promise<SyncTemplatesResult> {
  const body = await adminFetch<DataEnvelope<SyncTemplatesResult>>(
    '/admin/whatsapp/templates/sync',
    {
      method: 'POST',
      body: {} as Record<string, unknown>,
    },
  );
  return body.data;
}

// ─── Quality ────────────────────────────────────────────────────────────────

export interface QualitySnapshotResponse {
  readonly current: WhatsAppQualityState | null;
  readonly history: readonly WhatsAppQualityState[];
}

/** GET /admin/whatsapp/quality — current snapshot + (placeholder) 30-day history. */
export async function getWhatsAppQuality(): Promise<QualitySnapshotResponse> {
  const body = await adminFetch<DataEnvelope<QualitySnapshotResponse>>('/admin/whatsapp/quality');
  return body.data;
}

// ─── Cost summary ───────────────────────────────────────────────────────────

export interface CostSummaryResponse {
  readonly windowDays: number;
  readonly totalMessageCount: number;
  readonly estimatedCostUsdCents: number;
  readonly monthlyBudgetUsdCents: number;
  readonly optedInUserCount: number;
  readonly byCategory: ReadonlyArray<{
    readonly category: WhatsAppTemplateCategory;
    readonly count: number;
  }>;
  readonly byTemplate: ReadonlyArray<{
    readonly templateName: string;
    readonly count: number;
  }>;
}

/** GET /admin/whatsapp/cost-summary — rolling-30d aggregation widget. */
export async function getWhatsAppCostSummary(): Promise<CostSummaryResponse> {
  const body = await adminFetch<DataEnvelope<CostSummaryResponse>>('/admin/whatsapp/cost-summary');
  return body.data;
}

// ─── Delivery log ───────────────────────────────────────────────────────────

export interface DeliveryLogEntry {
  readonly id: string;
  readonly inboxNotificationId: string;
  readonly recipientUserId: string;
  readonly sourceEventKind: string;
  readonly sourceEventId: string;
  readonly templateName: string | null;
  readonly status:
    | 'delivered'
    | 'pending_retry'
    | 'permanent_failure'
    | 'suppressed_by_preference'
    | 'rate_limited'
    | 'no_active_tokens';
  readonly attemptCount: number;
  readonly deliveryState: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  readonly whatsappMessageId: string | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly lastAttemptAt: string | null;
  readonly nextAttemptAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface DeliveryLogResponse {
  readonly data: readonly DeliveryLogEntry[];
}

export interface ListDeliveryLogOpts {
  readonly userId?: string;
  readonly bookingId?: string;
  readonly limit?: number;
}

/**
 * GET /admin/whatsapp/delivery-log
 * — server requires at least one of userId or bookingId.
 */
export async function listWhatsAppDeliveryLog(
  opts: ListDeliveryLogOpts,
): Promise<DeliveryLogResponse> {
  const qs = buildQuery({
    userId: opts.userId,
    bookingId: opts.bookingId,
    limit: opts.limit,
  });
  return adminFetch<DeliveryLogResponse>(`/admin/whatsapp/delivery-log${qs}`);
}
